/**
 * Dental Stage 1: Discharge Information — PDF component and field definitions.
 *
 * Auto-filled: clinic header, patient name throughout.
 * Manual fields: medication choice, dynamic medication list, extraction assessment.
 */
import { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer'

import { ClinicHeader } from '@/frontend/features/printing/documents/blocks/clinic-header'
import {
  CheckboxField,
  InlineField,
} from '@/frontend/features/printing/documents/blocks/pdf-fields'
import { baseStyles } from '@/frontend/features/printing/documents/blocks/pdf-styles'
import {
  type DischargeMedicationEntry,
  dentalClosingText,
  MEDICATION_SIDE_EFFECTS,
  medicationText,
  OUT_OF_HOURS,
  restText,
  THINGS_TO_NOTICE,
} from '@/frontend/features/printing/documents/discharge/shared'
import type { DocumentContext } from '@/frontend/features/printing/documents/types'
import { mm } from '@/frontend/features/printing/pdf-utils'

export interface DentalDischargeStage1Fields {
  hasMedication: 'none' | 'has_meds' | ''
  medications: DischargeMedicationEntry[]
  extractionAssessment: 'none' | 'requires' | ''
  extractionsDescription: string
}

export const defaultDentalDischargeStage1Fields: DentalDischargeStage1Fields = {
  hasMedication: '',
  medications: [],
  extractionAssessment: '',
  extractionsDescription: '',
}

export function procedureText(name: string) {
  return `${name} has had a general anaesthetic for a dental procedure. We scaled and polished ${name}'s teeth, performed mouth X-rays to visualise tooth roots and thoroughly examined for any dental abnormalities.`
}

export function feedingText(name: string) {
  return `When you go home today, offer ${name} a bland, soft diet such as boiled chicken and rice or a commercial diet such as Royal Canin Gastrointestinal. We recommend offering small portions at a time as sometimes the anaesthetic drugs can cause nausea. We recommend feeding a soft food diet for the next 2-3 days until any inflammation has subsided. Provide fresh water at all times.`
}

export function noExtractionsText(name: string) {
  return `Based on our findings today ${name} does not require any extractions.`
}

export function requiresExtractionsText(name: string) {
  return `Based on our findings today ${name} requires`
}

export const EXTRACTION_SCHEDULING =
  'This will be performed in a separate procedure in 4-6 weeks time. This can be booked with our team today.'

export function followUpText(name: string) {
  return `Today's procedure went smoothly, and there is no need for an in person follow up appointment but one of our team will check in to see how ${name} is doing in 3 days.`
}

const styles = StyleSheet.create({
  ...baseStyles,
})

export function DentalDischargeStage1Pdf({
  context,
  fields,
}: {
  context: DocumentContext
  fields: DentalDischargeStage1Fields
}) {
  const { patient, clinic } = context
  const name = patient.name
  const meds = fields.medications.filter((m) => m.name)

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <ClinicHeader clinic={clinic} />

        <Text style={styles.heading}>Dental Stage 1: Discharge Information</Text>

        <Text style={styles.text}>
          <Text style={styles.bold}>Procedure: </Text>
          {procedureText(name)}
        </Text>

        <Text style={styles.heading}>Rest and activity:</Text>
        <Text style={styles.text}>{restText(name)}</Text>

        <Text style={styles.heading}>Feeding:</Text>
        <Text style={styles.text}>{feedingText(name)}</Text>

        <Text style={styles.heading}>Medication:</Text>
        <Text style={styles.text}>{medicationText(name)}</Text>

        {fields.hasMedication === 'none' ? (
          <CheckboxField checked label={`${name} does not have any medication to go home.`} />
        ) : fields.hasMedication === 'has_meds' ? (
          <View>
            <CheckboxField
              checked
              label={`${name} has medication to go home and are as follows:`}
            />
            {meds.map((med) => (
              <Text key={med.key} style={styles.listItem}>
                <Text style={styles.bold}>• {med.name}</Text>
                {med.instructions ? ` — ${med.instructions}` : ''}
              </Text>
            ))}
          </View>
        ) : null}

        <Text style={styles.text}>{MEDICATION_SIDE_EFFECTS}</Text>

        <Text style={styles.heading}>Follow up appointments:</Text>
        {fields.extractionAssessment === 'none' ? (
          <CheckboxField checked label={noExtractionsText(name)} />
        ) : fields.extractionAssessment === 'requires' ? (
          <View>
            <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: mm(2) }}>
              <CheckboxField checked label={requiresExtractionsText(name)} />
              <InlineField value={fields.extractionsDescription} />
              <Text style={styles.text}>extraction(s).</Text>
            </View>
            <Text style={styles.text}>{EXTRACTION_SCHEDULING}</Text>
          </View>
        ) : null}
        <Text style={styles.text}>{followUpText(name)}</Text>

        <Text style={styles.heading}>Other things you may notice:</Text>
        {THINGS_TO_NOTICE.map((item) => (
          <Text key={item.slice(0, 30)} style={styles.listItem}>
            • {item}
          </Text>
        ))}

        <Text style={styles.heading}>Out of hours contact:</Text>
        <Text style={styles.text}>{OUT_OF_HOURS}</Text>

        <View style={styles.sectionGap}>
          <Text style={styles.text}>{dentalClosingText(name)}</Text>
          <Text style={styles.text}>Snoots Vet, North London.</Text>
        </View>
      </Page>
    </Document>
  )
}
